import {
  ConnectionTypeEnum,
  EICStatusEnum,
  MessageDirectionEnum,
  RequestStatusEnum,
  RequestTypeEnum,
  ServiceTypeEnum,
} from "../Premises/types";
import { Appliance } from "./FormComponents/Calculator";
import { MessageAttachment, MessageTypeEnum } from "./Messages/types";
import {
  InternetRequestRawStatusEnum,
  InternetRequestStatusEnum,
} from "./ServiceTypeStatusAlert";

export interface CloseTransferFile {
  file: File;
  fileName: string;
}

export interface ElectricityBlockValues {
  licensedElectrician: string;
  licensedElectricianDetails: ChangeRequestContact | null;
  aspDetails: ChangeRequestContact | null;
  connectionType: ConnectionTypeEnum | null;
  meterProvider: string;
  asp: string;
  maxLoad: string;
  phases: string;
  transformer: boolean;
  showAsp: boolean;
  moreThan100Consumption: boolean;
  truckAppointment: boolean;
  truckAppointmentReason: string;
  nmi: string;
  installationType: string;
  locationDescription: string;
  otherLocationDescription: string;
  solar: string;
  sizeOfSolarSystem: string;
  offpeakRequirements: string[];
  otherOffpeakRequirement: string;
  rexId: string;
  coes: string;
  ntc: string | null;
  state?: string;
  isNmiRequired: boolean;
  coesFiles?: UploadedFile[];
  mcoesFiles?: UploadedFile[];
  ewrFiles?: UploadedFile[];
  //premise-block-electricity
  electricityAccountPayer: AccountPayer;
  electricityCollaboratorId: string;
  assignElectricityRequirements: boolean;
  //lodged request metadata
  electricityRequesterName?: string;
  electricityRequesterFirstName?: string;
  electricityRequesterLastName?: string;
  electricityRequesterEmail?: string;
  electricitySubmittedDateTime?: Date;
  allocateNmiOrderNumber?: string;
  meterServiceOrderNumber?: string;
  electricitySupplyServiceOrderNumber?: string;
  electricityNetworkFormNumber?: string;
  electricityLifeSupport?: boolean;
  electricityEicStatus?: EICStatusEnum;
  //home-user values
  homeUserLicencedElectrician?: {
    firstName: string;
    lastName: string;
    companyName: string;
    phoneNumber: string;
    email: string;
    licenseNumber: string;
  };
  homeUserAsp?: {
    firstName: string;
    lastName: string;
    licenseNumber: string;
  };
}
export interface InternetBlockValues {
  internetConnectionType: ConnectionTypeEnum | null;
  //premise-block-internet
  internetAccountPayer: AccountPayer;
  internetCollaboratorId: string;
  assignInternetRequirements: boolean;
  internetEicStatus?: EICStatusEnum;
  //lodged request metadata
  internetRequesterName?: string;
  internetRequesterFirstName?: string;
  internetRequesterLastName?: string;
  internetRequesterEmail?: string;
  internetSubmittedDateTime?: Date;
  internetHandoverDate?: string;
  internetPreinstallationDate?: string;
  internetNbnAddress?: string;
  internetCloseByName?: string;
  internetCloseByDate?: Date;
  internetCancelByName?: string;
  internetCancelByDate?: Date;
  internetSiteSupervisor?:
    | string
    | {
        first_name?: string;
        last_name?: string;
        phone_number?: string;
        email?: string;
      };
  internetPreInstaller?:
    | string
    | {
        first_name?: string;
        last_name?: string;
        phone_number?: string;
        email?: string;
      };
  internetRequestId?: string;
  internetJobId?: string;
  internetKnockdownFutureState?: string;
  internetAnexAddress?: string;
  internetRequestRawStatus?: InternetRequestRawStatusEnum | null;
  internetComputedRequestStatus?: RequestStatusEnum | null;
}
export interface GasOptionValues {
  accountPayer: AccountPayer;
  assignRequirements: boolean;
  collaboratorId: string;
  licensedPlumber: string;
  homeUserLicencedPlumber?: {
    firstName: string;
    lastName: string;
    companyName: string;
    phoneNumber: string;
    email: string;
    licenseNumber: string;
  };
  homeUserSiteSupervisor?: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
  };
  pressure: string;
  otherPressure: string;
  totalLoad: string;
  totalLoadSummary: string;
  totalLoadDetails: Appliance[];
  siteConditions: string[];
  otherSiteCondition: string;
  meterLocation: string;
  otherMeterLocation: string;
  //lodged request metadata
  requesterName?: string;
  submittedDateTime?: Date;
  allocateNmiOrderNumber?: string;
  meterServiceOrderNumber?: string;
  supplyServiceOrderNumber?: string;
  networkFormNumber?: string;
  appliancesChecklistFiles?: UploadedFile[];
  sitePlanFiles?: UploadedFile[];
  shipperDocumentsFiles?: UploadedFile[];
  eicStatus?: EICStatusEnum;
}

export interface GasBlockValues {
  gasOptions: GasOptionEnum[];
  [GasOptionEnum.BOTH]: GasOptionValues & {
    buildingType: string;
    lifeSupport?: boolean;
  };
  [GasOptionEnum.GAS_METER]: GasOptionValues & {
    mirn: string;
    preferDate: Date | null;
    preferTime: string;
    access: string;
    cocNumber: string;
    swnNumber: string;
    isUpdatesOnGasLine: string;
    lifeSupport?: boolean;
  };
  [GasOptionEnum.GAS_LINE]: GasOptionValues & {
    buildingType: string;
    siteLockupDate: Date | null;
  };
}

export type Contact = {
  email: string;
  first_name: string;
  id: string;
  last_name: string;
  license_number: string;
  phone_number: string;
};

export type ChangeRequestContact = {
  id?: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  licenceNumber: string;
  companyName?: string;
  contactType: string;
};

export interface AccountBlockValues {
  accountHolderDetailsType: string;
  accountId: string | null;
  accountHolder: string;
  secondaryAccountHolder: boolean;
  abn: string;
  companyName?: string;
  accountPrimaryFirstName: string;
  accountPrimaryLastName: string;
  accountPrimaryDob: Date | null;
  accountPrimaryPhone: string;
  accountPrimaryEmail: string;
  accountSecondaryFirstName: string;
  accountSecondaryLastName: string;
  accountSecondaryDob: Date | null;
  accountSecondaryPhone: string;
  accountSecondaryEmail: string;
  accountCommsPrefs: string;
  addSecondaryNameToBill: boolean;
  customerConsentFiles: UploadedFile[];
  claimsCustomerConsentFiles: MessageAttachment[];
  sameAsConnectionAddress: boolean;
  "billing-address": BillingAddressValues;
  billingAddressString: string;
  homeUserAgreeTerms: boolean;
}

export type BillingAddressValues = {
  suburb: string;
  state: string;
  postcode: string;
  pobox: boolean;
  unitNumber: string;
  premiseType: string;
  streetNumber: string;
  streetName: string;
  level: string;
  streetSuffix: string;
  streetType: string;
  buildingName: string;
  poboxNumber: string;
  poboxType: string;
};

export interface PremiseBlockValues {
  id: string | null;
  locId: string;
  siteSupervisor: string;
  siteSupervisors: Contact[];
  licensedPlumbers: Contact[];
  requestingCompany: RequestingCompany | null;
  projectId: string;
  projectName: string;
  propertyType: string;
  premiseAddress: PremiseAddressValues;
  premiseAddressString: string;
  propertyAccess: string;
  preventingAccess: string;
  serviceAssignee: string;
  selectedConnections: EnergyType[];
  internetRequestStatus: InternetRequestStatusEnum | null;
  questionnaireKnockdown: "yes" | "no";
  questionnaireLocation: "yes" | "no";
  questionnaireDemolition: "yes" | "no";
  questionnaireHomePrepareCheck: "yes" | "no";
  isDisablePremiseButton: boolean;
  isShowInternetSectionInPremise: boolean;
}

export interface CloseTransferValues {
  closeTransfer: {
    isAccountHolderEditable: boolean;
    handoverType: HandoverTypeEnum;
    elecPreferDate: Date | null;
    gasPreferDate: Date | null;
    serviceTypes: ServiceTypeEnum[];
    // legacy flow
    signedTransferFormFiles?: CloseTransferFile[];
    fileMeta?: UploadedFile;
    // new flow
    accountHolder?: {
      firstName: string | undefined;
      lastName: string | undefined;
      phone: string | undefined;
      email: string | undefined;
    };
    isCustomerConsentChecked?: boolean;
    isShowCustomerConsentCheckbox?: boolean;
  };
}

export enum GasOptionEnum {
  BOTH = "both",
  GAS_METER = "gasMeter",
  "GAS_LINE" = "gasLine",
}

export interface PremiseAddressValues {
  siteId: string;
  state: string;
  buildingName: string;
  suburb: string;
  postcode: string;
  unitNumber: string;
  premiseType: string;
  streetNumber: string;
  lotNumber: string;
  streetName: string;
  grannyFlat: boolean;
  level: string;
  dpNumber: string;
  streetSuffix: string;
  streetType: string;
  sectionNumber: string;
  blockNumber: string;
}

export interface FormValues
  extends ElectricityBlockValues,
    GasBlockValues,
    AccountBlockValues,
    InternetBlockValues,
    CloseTransferValues,
    InternetBlockValues,
    PremiseBlockValues {}

// Enum for ServiceType
export enum ServiceType {
  ELECTRICITY = "ELECTRICITY",
  GAS = "GAS",
  INTERNET = "INTERNET",
}

// Enum for FileUploadType
enum FileUploadType {
  COMPANY = "COMPANY",
  SOMEONE_ELSE = "SOMEONE_ELSE",
  ME_MY_COMPANY = "ME_MY_COMPANY",
}

// AttachmentMeta Interface
export interface AttachmentMeta {
  id: string;
  file_upload_type: FileUploadType;
  file_name: string;
  file_upload_type_affix?: string;
  link?: string | null;
}

// AccountPayer Enum
export enum AccountPayer {
  COMPANY = "COMPANY",
  SOMEONE_ELSE = "SOMEONE_ELSE",
  ME_MY_COMPANY = "ME_MY_COMPANY",
}

// Service Interface
export interface Service {
  draft_id: string;
  draft_version?: string; // optional
  request_id: string;
  collaborator_id: string;
  account_payer: AccountPayer;
  service_type: ServiceType;
  // payload: Record<string, any>; // generic object
  payload: string; // generic object
  attachments_meta?: AttachmentMeta[]; // optional
  journey_id?: string;
  raw_request_status?: string;
  request_status?: RequestStatusEnum;
  request_type?: RequestTypeEnum;
  latest_message_direction?: MessageDirectionEnum;
  latest_message_processed?: boolean;
  latest_message_type?: MessageTypeEnum;
  has_unread_message_for_user?: boolean;
  message_pause?: boolean;
  message_enabled?: boolean;
  connection_type?: ConnectionTypeEnum;
  channel: string;
  distributor_application_id?: string;
  eic_status?: EICStatusEnum;
  auto_submit?: boolean;
  comm_sent_date_time?: Date;
  life_support?: boolean;
  handover_date?: string;
  preinstallation_date?: string;
}

export interface LodgedElectricityService extends Service {
  allocate_nmi_order_number?: string;
  meter_service_order_number?: string;
  supply_service_order_number?: string;
  requestor?: string;
  requestor_email?: string;
  requestor_first_name?: string;
  requestor_last_name?: string;
  submitted_date_time?: Date;
  network_form_number?: string;
}

export interface DraftServiceFormData {
  accountId?: string;
  service: Service;
  attachments: File[];
}

export interface RequestingCompany {
  msaNumber: string;
  id: string;
  name: string;
  operationalTeam: string;
}

export interface RequestingCompanyResponse {
  msa_number: string;
  id: string;
  name: string;
  operational_team: string;
}

export type EnergyType = "Gas" | "Electricity" | "Internet";

export enum DocumentTypeEnum {
  ELECTRICAL_WORKS_REQUEST = "ELECTRICAL_WORKS_REQUEST",
  ELECTRICAL_WORKS_REQUEST_2 = "ELECTRICAL_WORKS_REQUEST_2",
  CERTIFICATE_OF_ELECTRICAL_SAFETY = "CERTIFICATE_OF_ELECTRICAL_SAFETY",
  ADDITIONAL_CERTIFICATE_OF_ELECTRICAL_SAFETY = "ADDITIONAL_CERTIFICATE_OF_ELECTRICAL_SAFETY",
  MAINS_CERTIFICATE_OF_ELECTRICAL_SAFETY = "MAINS_CERTIFICATE_OF_ELECTRICAL_SAFETY",
  SITE_PLAN = "SITE_PLAN",
  PHOTOS = "PHOTOS",
  QUOTE = "QUOTE",
  PLAN_OF_SUBDIVISION = "PLAN_OF_SUBDIVISION",
  NEW_ENERGY_ACCOUNT_FORM = "NEW_ENERGY_ACCOUNT_FORM",
  HANDOVER_CONSENT_FORM = "HANDOVER_CONSENT_FORM",
  VESI_ABOLISHMENT_FORM = "VESI_ABOLISHMENT_FORM",
  SOLAR_PV_AGREEMENT = "SOLAR_PV_AGREEMENT",
  APPLIANCE_CHECKLIST = "APPLIANCE_CHECKLIST",
  OTHER = "OTHER",
  MAINS_CERTIFICATE_OF_ELECTRICAL_SAFETY_2 = "MAINS_CERTIFICATE_OF_ELECTRICAL_SAFETY_2",
  SHIPPER_DOCUMENTS = "SHIPPER_DOCUMENTS",
}

export interface UploadedFile {
  name: string;
  attachmentId: string;
  isUploading?: boolean;
  progress?: number;
  fileType: DocumentTypeEnum;
  link?: string;
}

export interface AttachmentResponse {
  id: string;
  file_upload_type: DocumentTypeEnum;
  file_upload_type_affix: string;
  file_name: string;
  link?: string;
}
export interface ClaimsAttachment {
  file_upload_type: DocumentTypeEnum;
  file_name: string;
  link?: string;
}

export enum HandoverTypeEnum {
  CLOSE_AND_TRANSFER = "CLOSE_AND_TRANSFER",
  CLOSE = "CLOSE",
}

export enum CloseTransferStatusEnum {
  SUBMITTED = "SUBMITTED",
  PROCESSED = "PROCESSED",
  CANCELLED = "CANCELLED",
  NOT_REQUIRED = "NOT_REQUIRED",
}

export enum ErrorTypeEnum {
  OCP_FIELD_VALIDATION = "FIELD_VALIDATION",
  KRAKEN_FIELD_VALIDATION = "KRAKEN_FIELD_VALIDATION",
  KRAKEN_TIMEOUT = "KRAKEN_TIMEOUT",
  LODGE_REQUEST_FAILED = "LODGE_REQUEST_FAILED",
}
